/**
 * The SSRF-safe HTTP client (M1.1.2–M1.1.5).
 *
 * Every outbound request in the product goes through this (CONTRIBUTING §1). It
 * is the other implementation of {@link HttpClient} — the fixture harness being
 * the first — so code under test cannot tell which end is attached.
 *
 * ## The pin is a `lookup` function, not a rewritten URL
 *
 * The obvious way to connect to a validated address is to put the address in
 * the URL: resolve `example.com` to `93.184.216.34`, then fetch
 * `https://93.184.216.34/`. That breaks both halves of TLS. SNI carries the
 * IP, so a server hosting many sites cannot pick the right certificate, and
 * certificate verification compares the IP against the certificate's names and
 * fails. The usual next step is to disable verification, at which point the
 * fix is worse than the hole it closed.
 *
 * So the hostname stays everywhere it is visible — in the URL, in the `Host`
 * header, in SNI, in the certificate check — and only the name-to-address step
 * is intercepted, through Node's `lookup` option. The socket connects to the
 * address we validated; the TLS handshake is against the name the publisher
 * owns. `client.net.test.ts` proves Node honours that, because the whole design
 * rests on it.
 *
 * ## Resolution is explicit DNS, not `getaddrinfo`
 *
 * `dns.resolve4`/`resolve6` query DNS. `dns.lookup` calls `getaddrinfo`, which
 * consults `/etc/hosts`, honours search domains, and — as M1.1.1 measured —
 * decodes `0251.0376.0251.0376` into 169.254.169.254. Using the resolver
 * directly means those conversions cannot happen here at all: a second layer
 * behind `host.ts`, which already refuses such hosts.
 *
 * ## No connection pooling
 *
 * `agent: false`. A pooled socket was opened to an address that was validated
 * for some earlier request, and reusing it skips validation for this one — the
 * same hole as re-resolving, arrived at from a different direction. The cost is
 * a handshake per request, which for a feed poller running every 15 minutes is
 * not a cost at all.
 */

import { Resolver } from 'node:dns/promises';
import {
  request as httpRequest,
  type ClientRequest,
  type IncomingMessage,
} from 'node:http';
import { request as httpsRequest } from 'node:https';
import type { Transform } from 'node:stream';
import { createBrotliDecompress, createGunzip, createInflate } from 'node:zlib';

import { assertPublicAddress, classifyAddress, parseIpAddress } from './address.js';
import {
  AmbiguousHostError,
  CredentialsInUrlError,
  DecompressionBombError,
  InsecureRedirectError,
  MalformedUrlError,
  NoUsableAddressError,
  RedirectLoopError,
  RequestTimeoutError,
  ResponseTooLargeError,
  TooManyRedirectsError,
  TransportError,
  UnsupportedSchemeError,
} from './errors.js';
import { assertFetchableHost } from './host.js';
import type { HttpClient, HttpRequestOptions, HttpResponse } from './types.js';

/* --------------------------------------------------------- configuration --- */

/**
 * How the crawler identifies itself (M1.1.5).
 *
 * Required, with no default. A crawler that does not say who it is and how to
 * complain gives a publisher no option between tolerating it and blocking it,
 * and D4's posture — we are a well-behaved client, always — is not something
 * to leave to a fallback value.
 */
export interface CrawlerIdentity {
  readonly product: string;
  readonly version: string;
  /** A page a publisher can read to find out what this is and how to stop it. */
  readonly contactUrl: string;
}

export interface ResourceLimits {
  /** Decompressed response body cap. Exceeding it aborts the connection. */
  readonly maxResponseBytes: number;
  readonly connectTimeoutMs: number;
  /** Covers the whole request including every redirect hop. */
  readonly totalTimeoutMs: number;
  readonly maxRedirects: number;
  /**
   * Ceiling on decompressed ÷ transferred bytes.
   *
   * **The size cap is the real bomb protection**, and this is a secondary
   * early abort. `maxResponseBytes` bounds the output regardless of ratio, and
   * bounding the output bounds both the memory and the work — so a bomb is
   * already stopped without this.
   *
   * The default is high because measurement said it had to be; see
   * {@link DEFAULT_LIMITS}.
   */
  readonly maxDecompressionRatio: number;
}

export const DEFAULT_LIMITS: ResourceLimits = {
  /*
   * 20 MB, raised from 5 MB on 2026-09-24 because **5 MB rejected a real feed
   * from our own seed set**: `danluu.com/atom.xml` is 6.4 MB of full-text
   * entries, and the cap meant it could never ingest at all — every poll
   * aborted the connection, the error count climbed, and the feed would
   * eventually have been marked unhealthy for the crime of being long.
   *
   * The original 5 MB was a guess. 20 MB is a measurement plus headroom: the
   * largest feed anybody has pointed this at is 6.4 MB, the fetcher has 1 GB
   * of memory and handles one message at a time, and a 20 MB body with its
   * parse tree is a small fraction of that. The decompression-ratio guard
   * still stops a bomb, and the cap still stops an endless response.
   *
   * It is deliberately *not* unbounded, and callers who have a person waiting
   * override it downwards — subscribe-time discovery uses 2 MB.
   */
  maxResponseBytes: 20 * 1024 * 1024,
  connectTimeoutMs: 5_000,
  totalTimeoutMs: 30_000,
  maxRedirects: 5,

  /*
   * Measured across the whole fixture corpus rather than guessed, and the
   * measurement moved this by two orders of magnitude:
   *
   *   volume/huge-item        2.0 MB    gzip  244:1    brotli 6225:1
   *   volume/thousand-items   402 KB    gzip   31:1    brotli   61:1
   *   everything else                   gzip  < 7:1    brotli   < 10:1
   *
   * Brotli reaching 6225:1 on legitimate — if synthetic — content is the
   * surprise. A cap of 100, which is the number that looks reasonable, would
   * refuse our own corpus; so would 1000. Anything low enough to be
   * interesting for brotli is low enough to reject a large repetitive feed,
   * which is a real shape.
   *
   * So the ratio is set above anything we have observed and the size cap does
   * the actual work. Note also that DEFLATE cannot exceed 1032:1 by
   * construction, so for gzip and deflate this bound never engages at all —
   * in practice it is a brotli-only backstop.
   */
  maxDecompressionRatio: 10_000,
};

/**
 * Below this many decompressed bytes the ratio is not checked.
 *
 * A 200-byte response that expands to 40 KB has a ratio of 200 and costs
 * nothing. Applying the cap to it would reject harmless responses to protect
 * against an expense that has not been incurred.
 */
const RATIO_CHECK_FLOOR_BYTES = 256 * 1024;

/** Seams. Replaced in tests; never in production. */
export interface SafeHttpClientDeps {
  /** Returns every address DNS has for a hostname, IPv4 and IPv6 together. */
  readonly resolve?: (hostname: string) => Promise<readonly string[]>;
  readonly request?: (
    scheme: 'http:' | 'https:',
    options: RequestShape,
    onResponse: (response: IncomingMessage) => void,
  ) => ClientRequest;
  readonly now?: () => number;
}

/** The request options this client builds, named so tests can assert on them. */
export interface RequestShape {
  readonly host: string;
  readonly servername: string;
  readonly port: number;
  readonly path: string;
  readonly method: 'GET';
  readonly headers: Record<string, string>;
  readonly lookup: PinnedLookup;
  readonly rejectUnauthorized: true;
  readonly agent: false;
}

export type PinnedLookup = (
  hostname: string,
  options: { all?: boolean; family?: number },
  callback: (
    error: Error | null,
    address: string | { address: string; family: number }[],
    family?: number,
  ) => void,
) => void;

export interface SafeHttpClientOptions {
  readonly identity: CrawlerIdentity;
  readonly limits?: Partial<ResourceLimits>;
  readonly deps?: SafeHttpClientDeps;
}

/* ------------------------------------------------------------- the client -- */

export function createSafeHttpClient(options: SafeHttpClientOptions): HttpClient {
  const limits: ResourceLimits = { ...DEFAULT_LIMITS, ...options.limits };
  const userAgent = formatUserAgent(options.identity);
  const now = options.deps?.now ?? (() => Date.now());
  const resolve = options.deps?.resolve ?? systemResolver();
  const send = options.deps?.request ?? realRequest;

  /**
   * Resolves a hostname and returns one address we are willing to use.
   *
   * Every address DNS returns is validated. Disallowed ones are filtered out
   * rather than failing the whole host: a publisher whose AAAA record is a
   * stale unique-local address is misconfigured, not hostile, and we were
   * never going to connect to it. What matters is that the address we *do*
   * connect to was validated, and that a host with nothing usable is refused.
   */
  async function resolvePinned(hostname: string): Promise<string> {
    const addresses = await resolve(hostname);
    const rejected: string[] = [];

    for (const candidate of addresses) {
      const parsed = parseIpAddress(candidate);
      if (parsed === undefined || classifyAddress(parsed) !== undefined) {
        rejected.push(candidate);
        continue;
      }
      return parsed.canonical;
    }

    throw new NoUsableAddressError(hostname, rejected);
  }

  async function fetch(url: string, request?: HttpRequestOptions): Promise<HttpResponse> {
    const maxRedirects = request?.maxRedirects ?? limits.maxRedirects;
    const deadline = now() + (request?.timeoutMs ?? limits.totalTimeoutMs);

    const visited = new Set<string>();
    const chain: string[] = [];
    let target = parseTarget(url);

    for (;;) {
      if (visited.has(target.href)) {
        throw new RedirectLoopError([...chain, target.href]);
      }
      visited.add(target.href);

      const classified = assertFetchableHost(target.hostname);
      const address =
        classified.kind === 'address'
          ? classified.host
          : await resolvePinned(classified.host);

      // Belt and braces: whatever route the address arrived by, it is
      // validated immediately before the socket is opened.
      assertPublicAddress(address);

      const raw = await once(target, address, request, deadline);
      const location = raw.headers.get('location');

      if (!isRedirect(raw.status) || location === undefined) {
        return {
          status: raw.status,
          headers: raw.headers,
          body: raw.body,
          url: target.href,
          redirects: chain,
        };
      }

      const next = redirectTarget(target, location);
      chain.push(target.href);

      if (chain.length > maxRedirects) {
        throw new TooManyRedirectsError([...chain, next.href]);
      }

      target = next;
    }
  }

  function once(
    target: URL,
    address: string,
    request: HttpRequestOptions | undefined,
    deadline: number,
  ): Promise<{ status: number; headers: ReadonlyMap<string, string>; body: Uint8Array }> {
    return new Promise((settle, fail) => {
      let finished = false;
      let outbound: ClientRequest | undefined;

      const finish = (action: () => void): void => {
        if (finished) return;
        finished = true;
        clearTimeout(connectTimer);
        clearTimeout(totalTimer);
        action();
      };

      const abort = (error: Error): void => {
        finish(() => {
          // Aborting rather than reading to the end: the point of a cap is to
          // stop paying, which means closing the socket.
          outbound?.destroy();
          fail(error);
        });
      };

      const remaining = deadline - now();
      if (remaining <= 0) {
        fail(new RequestTimeoutError('total', limits.totalTimeoutMs, target.href));
        return;
      }

      const connectTimer = setTimeout(
        () => {
          abort(new RequestTimeoutError('connect', limits.connectTimeoutMs, target.href));
        },
        Math.min(limits.connectTimeoutMs, remaining),
      );

      const totalTimer = setTimeout(() => {
        abort(new RequestTimeoutError('total', limits.totalTimeoutMs, target.href));
      }, remaining);

      const scheme = target.protocol === 'https:' ? 'https:' : 'http:';

      const shape: RequestShape = {
        // The hostname, not the address: this becomes the Host header and the
        // identity the certificate is checked against.
        host: target.hostname,
        servername: target.hostname,
        port: Number(target.port) || (scheme === 'https:' ? 443 : 80),
        path: `${target.pathname}${target.search}`,
        method: 'GET',
        headers: buildHeaders(userAgent, request?.headers),
        lookup: pinnedLookup(address),
        rejectUnauthorized: true,
        agent: false,
      };

      try {
        outbound = send(scheme, shape, (response) => {
          clearTimeout(connectTimer);
          readBody(response, target, limits, {
            done: (body) =>
              finish(() =>
                settle({
                  status: response.statusCode ?? 0,
                  headers: headerMap(response),
                  body,
                }),
              ),
            failed: abort,
          });
        });
      } catch (error) {
        abort(new TransportError(target.href, error));
        return;
      }

      outbound.on('socket', (socket) => {
        const connected = (): void => clearTimeout(connectTimer);
        socket.once('connect', connected);
        socket.once('secureConnect', connected);
      });

      outbound.on('error', (error) => {
        abort(error instanceof Error ? new TransportError(target.href, error) : error);
      });

      outbound.end();
    });
  }

  return { fetch };
}

/* --------------------------------------------------------------- pinning --- */

/**
 * A `lookup` that answers with the validated address and ignores the name.
 *
 * This is the whole mechanism. Node calls it instead of resolving, so there is
 * no second resolution for a rebinding attack to win — the address that was
 * validated is the address the socket connects to, by construction rather than
 * by timing.
 *
 * Both call shapes are handled. `net.connect` uses `all: true` when
 * `autoSelectFamily` is on (the default since Node 20) and `all: false`
 * otherwise, and a client that handled only one would break on a version bump.
 *
 * Note that Node does not call `lookup` at all when `host` is already an IP
 * literal — there is no name to resolve. That is harmless here: the literal is
 * the address we validated, so there is nothing for a second resolution to
 * change.
 */
export function pinnedLookup(address: string): PinnedLookup {
  const parsed = parseIpAddress(address);
  const family = parsed?.family === 'ipv6' ? 6 : 4;

  return (_hostname, options, callback) => {
    if (options.all === true) {
      callback(null, [{ address, family }]);
      return;
    }
    callback(null, address, family);
  };
}

/* ------------------------------------------------------------ URL handling -- */

/**
 * Parses and vets a URL before anything is resolved or connected.
 *
 * ## The invariant that makes this safe
 *
 * **We validate and connect to the same normalised value, and never
 * re-interpret the URL string.** That sounds obvious and is exactly what the
 * natural implementation gets wrong, because there are two numeric-host
 * parsers in this runtime and they disagree:
 *
 * ```
 * input          new URL().hostname   dns.lookup
 * 0177.0.0.1     127.0.0.1            177.0.0.1
 * 010.0.0.1      8.0.0.1  (public)    10.0.0.1  (RFC 1918)
 * ```
 *
 * `new URL` reads a leading-zero label as octal; `getaddrinfo` reads it as
 * decimal. So a client that validates `URL.hostname` and then hands the
 * original host string to `getaddrinfo` validates 8.0.0.1 and connects to
 * 10.0.0.1. Here, resolution only ever receives `URL.hostname`, and an IP
 * literal is never resolved at all.
 *
 * Even so, a URL whose host the parser *rewrote* is refused rather than
 * fetched: quietly requesting 8.0.0.1 because someone typed `010.0.0.1` is a
 * surprise, and refusing ambiguity is the same rule `host.ts` applies.
 */
export function parseTarget(url: string): URL {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new MalformedUrlError(url);
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    // `file:`, `gopher:`, `data:` and the rest. A feed that links to one is
    // not a feed we follow.
    throw new UnsupportedSchemeError(parsed.protocol, url);
  }

  /*
   * `http://expected.example@evil.example/` has a host of evil.example and
   * reads, to a human and to a naive log line, as expected.example. Refused
   * rather than stripped: a URL in this shape is not one a publisher
   * published, and quietly cleaning it would hide that.
   */
  if (parsed.username !== '' || parsed.password !== '') {
    throw new CredentialsInUrlError(url);
  }

  assertHostNotRewritten(url, parsed);

  return parsed;
}

/**
 * Refuses a URL whose numeric host the parser rewrote.
 *
 * Only fires when the normalised host is an IP literal, so IDN punycoding
 * (`café.com` becoming `xn--caf-dma.com`) and case folding are unaffected —
 * those are rewrites of a *name*, which is not the ambiguity in question.
 */
function assertHostNotRewritten(url: string, parsed: URL): void {
  if (parseIpAddress(parsed.hostname.replace(/^\[|\]$/g, '')) === undefined) {
    return;
  }

  const authority = /^[A-Za-z][A-Za-z0-9+.-]*:\/\/([^/?#]*)/.exec(url)?.[1];
  if (authority === undefined) {
    return;
  }

  // Strip credentials (already refused above) and any port.
  const written = authority
    .slice(authority.lastIndexOf('@') + 1)
    .replace(/:\d*$/, '')
    .toLowerCase();

  if (written !== '' && written !== parsed.hostname.toLowerCase()) {
    throw new AmbiguousHostError(written, parsed.hostname);
  }
}

const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308]);
const isRedirect = (status: number): boolean => REDIRECT_STATUSES.has(status);

/** Resolves a `Location` against the URL that sent it, and vets the result. */
export function redirectTarget(from: URL, location: string): URL {
  let next: URL;
  try {
    next = new URL(location, from);
  } catch {
    throw new UnsupportedSchemeError('(unparseable Location)', location);
  }

  if (from.protocol === 'https:' && next.protocol === 'http:') {
    throw new InsecureRedirectError(from.href, next.href);
  }

  // Re-runs the scheme and credential checks on the new URL. The host itself
  // is re-validated by the next pass of the loop, resolution included.
  return parseTarget(next.href);
}

/* ------------------------------------------------------------------ body ---- */

interface BodySink {
  readonly done: (body: Uint8Array) => void;
  readonly failed: (error: Error) => void;
}

function readBody(
  response: IncomingMessage,
  target: URL,
  limits: ResourceLimits,
  sink: BodySink,
): void {
  const encoding = (response.headers['content-encoding'] ?? '').toLowerCase().trim();
  const decompressor = decompressorFor(encoding);

  if (decompressor === 'unsupported') {
    sink.failed(
      new TransportError(
        target.href,
        new Error(`unsupported content-encoding "${encoding}"`),
      ),
    );
    response.destroy();
    return;
  }

  const chunks: Buffer[] = [];
  let transferred = 0;
  let produced = 0;
  let stopped = false;

  const stop = (error: Error): void => {
    if (stopped) return;
    stopped = true;
    response.destroy();
    decompressor?.destroy();
    sink.failed(error);
  };

  // Counted on the wire, so an oversized transfer is stopped even if it would
  // have decompressed to something small.
  response.on('data', (chunk: Buffer) => {
    transferred += chunk.length;
    if (transferred > limits.maxResponseBytes) {
      stop(new ResponseTooLargeError(limits.maxResponseBytes, target.href));
    }
  });

  const stream = decompressor ?? response;

  stream.on('data', (chunk: Buffer) => {
    if (stopped) return;
    produced += chunk.length;

    if (produced > limits.maxResponseBytes) {
      stop(new ResponseTooLargeError(limits.maxResponseBytes, target.href));
      return;
    }

    if (
      decompressor !== undefined &&
      produced > RATIO_CHECK_FLOOR_BYTES &&
      transferred > 0 &&
      produced / transferred > limits.maxDecompressionRatio
    ) {
      stop(
        new DecompressionBombError(
          transferred,
          produced,
          limits.maxDecompressionRatio,
          target.href,
        ),
      );
      return;
    }

    chunks.push(chunk);
  });

  stream.on('end', () => {
    if (stopped) return;
    stopped = true;
    sink.done(new Uint8Array(Buffer.concat(chunks)));
  });

  stream.on('error', (error: Error) => {
    stop(new TransportError(target.href, error));
  });

  if (decompressor !== undefined) {
    response.on('error', (error: Error) => stop(new TransportError(target.href, error)));
    response.pipe(decompressor);
  }
}

/** Every zlib decompressor is a Transform, which is all this module needs. */
type Decompressor = Transform;

function decompressorFor(encoding: string): Decompressor | undefined | 'unsupported' {
  switch (encoding) {
    case '':
    case 'identity':
      return undefined;
    case 'gzip':
    case 'x-gzip':
      return createGunzip();
    case 'deflate':
      return createInflate();
    case 'br':
      return createBrotliDecompress();
    default:
      // We chose Accept-Encoding, so anything else is the server ignoring it,
      // and the bytes would be undecodable rather than merely unexpected.
      return 'unsupported';
  }
}

/* ---------------------------------------------------------------- headers --- */

export function formatUserAgent(identity: CrawlerIdentity): string {
  return `${identity.product}/${identity.version} (+${identity.contactUrl})`;
}

/**
 * Headers a caller may not set (D4, M3.5.2).
 *
 * Exported so the no-bypass guard can assert the set rather than trusting a
 * comment, and so a future addition has to be a deliberate edit here.
 */
export const NON_OVERRIDABLE_HEADERS: ReadonlySet<string> = new Set([
  'user-agent',
  'cookie',
  'referer',
  'accept-encoding',
]);

function buildHeaders(
  userAgent: string,
  caller: Readonly<Record<string, string>> | undefined,
): Record<string, string> {
  const headers: Record<string, string> = {
    'user-agent': userAgent,
    // Only encodings this client can actually decode.
    'accept-encoding': 'gzip, deflate, br',
    accept:
      'application/atom+xml, application/rss+xml, application/xml, application/json, text/xml;q=0.9, text/html;q=0.8, */*;q=0.5',
  };

  /*
   * Caller headers last, so conditional-request headers are passed through
   * (M1.1.5) — but four are not overridable, and three of those are D4
   * (M3.5.2).
   *
   * `user-agent`, because a caller that could replace it could spoof a
   * crawler. `cookie`, because injecting a session is how a paywall is
   * bypassed, and a client that *can* send one will eventually be asked to.
   * `referer`, because a forged search-engine referral is the same bypass
   * wearing a different hat — many sites open metered articles to visitors
   * who appear to arrive from a search result.
   *
   * These are dropped rather than rejected. A caller passing one is a bug to
   * find in review, not a request to fail at runtime, and failing the fetch
   * would turn a policy violation into an outage.
   *
   * `accept-encoding` is here for an unrelated reason: we can only decode
   * what we asked for.
   */
  for (const [name, value] of Object.entries(caller ?? {})) {
    const lower = name.toLowerCase();
    if (NON_OVERRIDABLE_HEADERS.has(lower)) {
      continue;
    }
    headers[lower] = value;
  }

  return headers;
}

/** Response headers, lowercased, with repeats joined as HTTP allows. */
function headerMap(response: IncomingMessage): ReadonlyMap<string, string> {
  const headers = new Map<string, string>();

  for (const [name, value] of Object.entries(response.headers)) {
    if (value === undefined) continue;
    headers.set(name.toLowerCase(), Array.isArray(value) ? value.join(', ') : value);
  }

  return headers;
}

/* -------------------------------------------------------------- transport -- */

function realRequest(
  scheme: 'http:' | 'https:',
  options: RequestShape,
  onResponse: (response: IncomingMessage) => void,
): ClientRequest {
  const send = scheme === 'https:' ? httpsRequest : httpRequest;
  return send(options as never, onResponse);
}

/**
 * Explicit DNS, both families, union of the answers.
 *
 * A fresh {@link Resolver} rather than the module-level functions so that the
 * client owns its resolution — the process-wide `dns.setServers` cannot be
 * changed underneath it by unrelated code.
 */
function systemResolver(): (hostname: string) => Promise<readonly string[]> {
  const resolver = new Resolver();

  return async (hostname: string) => {
    const [v4, v6] = await Promise.allSettled([
      resolver.resolve4(hostname),
      resolver.resolve6(hostname),
    ]);

    const addresses = [
      ...(v4.status === 'fulfilled' ? v4.value : []),
      ...(v6.status === 'fulfilled' ? v6.value : []),
    ];

    if (addresses.length === 0) {
      // Both families failed. Surface the IPv4 reason, which is the one an
      // operator will recognise.
      throw v4.status === 'rejected' ? v4.reason : new NoUsableAddressError(hostname, []);
    }

    return addresses;
  };
}
